<template>
  <div v-if="isOpen" class="payment-modal-overlay" @click.self="closeModal">
    <div class="payment-modal">
      <div class="payment-modal-header">Бронирование</div>

      <button class="payment-modal-close" type="button" @click="closeModal">
        <img :src="closeIcon" alt="Закрыть" />
      </button>

      <div v-if="atmosWait.active" class="payment-atmos-wait">
        <div v-if="!atmosWait.timedOut" class="payment-atmos-spinner"></div>
        <p v-if="!atmosWait.timedOut" class="payment-expired-text">
          Оплата открыта в новой вкладке. Ожидаем результат…
        </p>
        <p v-else class="payment-expired-text">
          Не удалось подтвердить оплату. Если вы уже оплатили в открытой вкладке, проверьте статус ещё раз.
        </p>
        <button class="payment-submit-button" type="button" @click="retryAtmosCheck">
          Проверить оплату ещё раз
        </button>
        <button v-if="atmosWait.timedOut" class="payment-expired-cancel" type="button" @click="reopenAtmosTab">
          Открыть страницу оплаты ещё раз
        </button>
        <button class="payment-expired-cancel" type="button" @click="cancelAtmosWait">
          Отменить
        </button>
      </div>

      <div v-else-if="timerExpired" class="payment-expired-alert">
        <div class="payment-expired-icon">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M16 3L2 28H30L16 3Z" stroke="#FF6B35" stroke-width="2" stroke-linejoin="round" />
            <path d="M16 13V19" stroke="#FF6B35" stroke-width="2" stroke-linecap="round" />
            <circle cx="16" cy="23" r="1.2" fill="#FF6B35" />
          </svg>
        </div>
        <h3 class="payment-expired-title">Запись отменена</h3>
        <p class="payment-expired-text">
          Мы не получили оплату, поэтому запись была отменена. Свяжитесь с администратором или забронируйте снова
        </p>
        <button class="payment-submit-button" type="button" @click="restartBooking">
          Записаться
        </button>
        <button class="payment-expired-cancel" type="button" @click="closeModal">
          Отмена
        </button>
      </div>

      <form v-else class="payment-form" @submit.prevent="submitPayment">
        <div class="booking-details-card">
          <div class="booking-details-date">{{ bookingDateLabel }}</div>
          <div class="booking-details-time">{{ bookingTimeLabel }}</div>
          <div class="booking-details-court">
            <BookingLocationIcon :size="14" :primary-color="bookingWidgetPrimaryColor" />
            <span>{{ bookingCourtLabel }}</span>
          </div>
        </div>

        <div class="select-field" :class="{ active: paymentSelectOpen }" tabindex="0" @blur="paymentSelectOpen = false">
          <div class="select-value" @click="paymentSelectOpen = !paymentSelectOpen">
            <div v-if="form.paymentType" class="payment-value">
              <div class="payment-value-logo">
                <img :src="getPaymentTypeIcon(form.paymentType.icon)" alt="" />
              </div>
              <div class="payment-value-name">{{ form.paymentType.label }}</div>
              <div v-if="form.paymentType.card" class="payment-value-param">
                *{{ form.paymentType.card.CardLastFour }}
              </div>
            </div>
            <div class="select-arrow">
              <img :src="arrowIcon" alt="" />
            </div>
          </div>

          <div class="select-items">
            <div
              v-for="(option, index) in paymentTypes"
              :key="index"
              class="select-item"
              :class="{ active: form.paymentType === option }"
              @click="selectPaymentType(option)"
            >
              <div class="payment-value">
                <div class="payment-value-logo">
                  <img :src="getPaymentTypeIcon(option.icon)" alt="" />
                </div>
                <div class="payment-value-name">{{ option.label }}</div>
                <div v-if="option.card" class="payment-value-param">
                  *{{ option.card.CardLastFour }}
                </div>
              </div>

              <div v-if="form.paymentType === option" class="select-item-active">
                <img :src="checkIcon" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div class="promocode-field" :class="{ 'promocode-error': promocodeData.success === false, applied: isPromocodeApplied }">
          <input
            v-model.trim="form.promocode"
            type="text"
            placeholder="Промокод"
            :disabled="isPromocodeApplied"
          />
          <button
            v-if="!isPromocodeApplied"
            type="button"
            class="promocode-accept"
            :class="{ disabled: !isPromocodeFilled }"
            :disabled="!isPromocodeFilled"
            @click="checkPromocode"
          >
            Применить
          </button>
          <button
            v-else
            type="button"
            class="promocode-reset"
            @click="resetPromocode"
          >
            <img :src="closeIcon" alt="Сбросить промокод" />
          </button>
          <div v-if="promocodeData.error" class="promocode-error-text">
            {{ promocodeData.error }}
          </div>
          <div v-else-if="isPromocodeApplied" class="promocode-success-text">
            Промокод применен
          </div>
        </div>

        <div v-if="showDepositToggle" class="bonus-field">
          <div class="bonus-field-value">
            {{ depositLabel }}
            <small>
              Доступно к списанию {{ availableDepositLabel }}
              <span>1 бонус = 1 рубль</span>
            </small>
          </div>
          <div class="bonus-field-control">
            <Toggle v-model="form.useDeposit" />
          </div>
        </div>

        <input
          v-if="!showYandexPaySplitWidget"
          v-model.trim="form.email"
          type="email"
          placeholder="Электронная почта для чека"
        />

        <div class="total-field">
          <div v-if="promocodeData.success && usablePromocode.value !== 0" class="total-field-row">
            <div class="key discount">Скидка по промокоду</div>
            <div class="value red">-<CurrencyValue :price="usablePromocode" /></div>
          </div>
          <div v-if="form.useDeposit && usableDeposit.value !== 0" class="total-field-row">
            <div class="key discount">Фитрубли</div>
            <div class="value red">-<CurrencyValue :price="usableDeposit" /></div>
          </div>
          <div class="total-field-row">
            <div class="key">Итого</div>
            <div class="value"><CurrencyValue :price="priceTotal" /></div>
          </div>
        </div>

        <YandexPaySplitWidget
          v-if="showYandexPaySplitWidget"
          :amount="priceTotal.value"
          :currency="priceTotal.currency"
          :settings="settings"
        />

        <div v-if="hasTimer" class="payment-timer">
          <span class="payment-timer-text">Успейте оплатить, чтобы подтвердить бронь</span>
          <span class="payment-timer-badge">{{ timerDisplay }}</span>
        </div>

        <button
          type="submit"
          class="payment-submit-button"
          :class="{ disabled: isDisabledBtn || !form.paymentType || isPaymentDataLoading || timerExpired }"
          :disabled="isDisabledBtn || !form.paymentType || isPaymentDataLoading || timerExpired"
        >
          Оплатить <CurrencyValue :price="priceTotal" />
        </button>
      </form>

      <div class="payment-hint" v-if="!timerExpired && !atmosWait.active && settings.modules?.offer?.link">
        Нажимая на кнопку, Вы соглашаетесь с
        <a :href="settings.modules.offer.link" target="_blank" rel="noopener noreferrer">Договором оферты</a>
      </div>

      <div v-if="!timerExpired && !atmosWait.active && errors.length !== 0" class="payment-errors">
        <ul>
          <li v-for="error in errors" :key="error">
            {{ error }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onUnmounted, ref, watch } from 'vue'
import Toggle from '@vueform/toggle'
import CurrencyValue from '@/widgets/booking/components/CurrencyValue.vue'
import YandexPaySplitWidget from '@/components/YandexPaySplitWidget.vue'
import {
  atmosRedirectPay,
  cloudPaymentsPayWidget,
  createAtmosTabWaiter,
  getPaymentReturnUrl,
  openAtmosPopupTab,
  PAYMENT_SCOPE_BOOKING_WIDGET,
  payselectionPayWidget,
  startYandexPayRedirect,
  tipTopPayWidget,
  yooKassaPayWidget
} from '@/utilities'
import { BookingAPI } from '@/widgets/booking/api'
import { normalizePrice, pickPreferredCurrency } from '@/widgets/booking/utils/currency'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import arrowIcon from '@/widgets/booking/assets/img/icons/arrow_right.svg'
import checkIcon from '@/assets/img/check.svg'
import BookingLocationIcon from '@/widgets/booking/components/icons/BookingLocationIcon.vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  bookingPayload: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
  currency: {
    type: String,
    default: 'RUB'
  }
})

const emit = defineEmits(['close', 'success'])

const settings = ref({})
const apiKey = ref('')
const cards = ref([])
const paymentTypes = ref([])
const paymentSelectOpen = ref(false)
const promocodeData = ref({ success: null, error: null })
const errors = ref([])
const isDisabledBtn = ref(false)
const isPaymentDataLoading = ref(false)
const timerSeconds = ref(0)
const timerIntervalId = ref(null)
const timerExpired = ref(false)
const hasTimer = ref(false)
const bookingDebtItem = ref(null)
const bookingStatus = ref('')
const bookingAutoDeleteTime = ref(0)
const discountApplied = ref(false)
const bookingWidgetPrimaryColor = computed(() => settings.value?.bookingWidget?.primaryWidgetColor || '#3C67D6')

const atmosWait = ref({ active: false, timedOut: false })
let capturedPaymentMethod = null
const atmosWaiter = createAtmosTabWaiter({
  setState: (next) => { atmosWait.value = next }
})

const form = ref({
  paymentType: null,
  promocode: '',
  email: '',
  useDeposit: false
})

const paymentCurrency = computed(() => {
  return [
    props.bookingPayload?.currency,
    settings.value?.currency,
    props.currency
  ].reduce((currency, fallbackCurrency) => pickPreferredCurrency(currency, fallbackCurrency))
})

watch(() => props.isOpen, async (isOpen) => {
  if (!isOpen) {
    stopTimer()
    hasTimer.value = false
    timerSeconds.value = 0
    timerExpired.value = false
    atmosWaiter.cancel()
    return
  }

  errors.value = []
  promocodeData.value = { success: null, error: null }
  paymentSelectOpen.value = false
  paymentTypes.value = []
  form.value.paymentType = null
  form.value.promocode = ''
  form.value.useDeposit = false
  form.value.email = userEmail.value
  bookingDebtItem.value = null
  bookingStatus.value = ''
  bookingAutoDeleteTime.value = 0
  discountApplied.value = false

  isPaymentDataLoading.value = true
  timerExpired.value = false
  hasTimer.value = false
  timerSeconds.value = 0
  atmosWaiter.cancel()

  try {
    await loadPaymentData()
  } finally {
    isPaymentDataLoading.value = false
  }
})

watch(() => form.value.promocode, (nextValue, prevValue) => {
  if (nextValue === prevValue) {
    return
  }

  if (promocodeData.value.success !== null || promocodeData.value.error !== null) {
    promocodeData.value = { success: null, error: null }
  }
})

const bookingPrice = computed(() => ({
  value: Number(props.bookingPayload?.price || 0),
  currency: paymentCurrency.value
}))

const userDeposit = computed(() => props.user?.depositFull || {
  value: 0,
  currency: paymentCurrency.value
})
const userEmail = computed(() => {
  const contacts = props.user?.contacts || []
  const emailContact = contacts.find((item) => item.contact_type === 'email')
  return emailContact?.contact || ''
})

const bookingDateLabel = computed(() => {
  if (!props.bookingPayload?.date) {
    return ''
  }

  const date = new Date(`${props.bookingPayload.date}T00:00:00`)
  const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'long' }).format(date)
  const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date)

  return `${capitalize(weekday)}, ${dayMonth}`
})

const bookingTimeLabel = computed(() => {
  if (!props.bookingPayload?.time) {
    return ''
  }

  const startTime = props.bookingPayload.time
  const durationMinutes = Number(props.bookingPayload?.duration || 0)

  if (!durationMinutes) {
    return `Время ${startTime}`
  }

  return `Время ${startTime} – ${addMinutes(startTime, durationMinutes)}`
})

const bookingCourtLabel = computed(() => {
  return props.bookingPayload?.courtName ? `${props.bookingPayload.courtName}` : ''
})

const usablePromocode = computed(() => {
  return normalizePrice(promocodeData.value.discount_amount, bookingPrice.value.currency)
})

const isPromocodeFilled = computed(() => Boolean(form.value.promocode))
const isPromocodeApplied = computed(() => promocodeData.value.success === true)

const usableDeposit = computed(() => {
  if (!form.value.useDeposit) {
    return { value: 0, currency: bookingPrice.value.currency }
  }

  const promocodeDiscount = promocodeData.value.success
    ? Number(usablePromocode.value.value || 0)
    : 0
  const priceAfterPromocode = Math.max(Number(bookingPrice.value.value || 0) - promocodeDiscount, 0)

  return {
    value: Math.min(Number(userDeposit.value?.value || 0), priceAfterPromocode),
    currency: bookingPrice.value.currency
  }
})

const priceTotal = computed(() => {
  let totalPrice = Number(bookingPrice.value.value || 0)

  if (form.value.useDeposit) {
    totalPrice -= Number(usableDeposit.value.value || 0)
  }

  if (promocodeData.value.success && usablePromocode.value.value) {
    totalPrice -= Number(usablePromocode.value.value || 0)
  }

  return {
    value: Math.max(totalPrice, 0),
    currency: bookingPrice.value.currency
  }
})

const showYandexPaySplitWidget = computed(() => {
  if (form.value.paymentType?.method === 'cash') {
    return false
  }

  return settings.value?.modules?.payment_types?.online?.acquiring_integration_type === 'yandex_pay'
})

const showDepositToggle = computed(() => {
  return Boolean(settings.value.modules?.balance?.enable && Number(userDeposit.value?.value || 0) > 0)
})

const depositLabel = computed(() => {
  return `${Number(userDeposit.value?.value || 0)} фитрублей`
})

const availableDepositLabel = computed(() => {
  return `${Number(userDeposit.value?.value || 0)} бонусов`
})

const timerDisplay = computed(() => {
  const minutes = Math.floor(timerSeconds.value / 60)
  const seconds = timerSeconds.value % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

function getPaymentTypeIcon(icon) {
  if (!icon) return null
  try {
    return require(`@/assets/img/payment_types/${icon}.png`)
  } catch (e) {
    return null
  }
}

function capitalize(value) {
  if (!value) {
    return ''
  }

  return value.charAt(0).toUpperCase() + value.slice(1)
}

function addMinutes(time, minutesToAdd) {
  const [hours, minutes] = time.split(':').map(Number)
  const totalMinutes = hours * 60 + minutes + minutesToAdd
  const nextHours = Math.floor(totalMinutes / 60) % 24
  const nextMinutes = totalMinutes % 60

  return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`
}

function getPaymentTimeoutSeconds() {
  const timeoutMinutes = Number(
    settings.value?.registration_pending_payment_time
    || settings.value?.modules?.schedule?.registration_pending_payment_time
    || 15
  )

  if (!Number.isFinite(timeoutMinutes) || timeoutMinutes <= 0) {
    return 15 * 60
  }

  return timeoutMinutes * 60
}

function startTimer(deadlineMs = null) {
  stopTimer()
  const fallbackDeadlineMs = Date.now() + (getPaymentTimeoutSeconds() * 1000)
  const effectiveDeadlineMs = deadlineMs && deadlineMs > Date.now() ? deadlineMs : fallbackDeadlineMs

  hasTimer.value = true

  timerIntervalId.value = window.setInterval(() => {
    const remainingSeconds = Math.max(0, Math.round((effectiveDeadlineMs - Date.now()) / 1000))
    timerSeconds.value = remainingSeconds

    if (remainingSeconds <= 0) {
      timerSeconds.value = 0
      timerExpired.value = true
      stopTimer()
      return
    }
  }, 1000)

  // Immediate first tick
  const initialRemaining = Math.max(0, Math.round((effectiveDeadlineMs - Date.now()) / 1000))
  timerSeconds.value = initialRemaining
  if (initialRemaining <= 0) {
    timerExpired.value = true
    stopTimer()
  }
}

function stopTimer() {
  if (timerIntervalId.value !== null) {
    window.clearInterval(timerIntervalId.value)
    timerIntervalId.value = null
  }
}

function closeModal() {
  emit('close')
}

function restartBooking() {
  stopTimer()
  timerExpired.value = false
  hasTimer.value = false
  timerSeconds.value = 0
  bookingDebtItem.value = null
  bookingStatus.value = ''
  bookingAutoDeleteTime.value = 0
  discountApplied.value = false
  errors.value = []
  isDisabledBtn.value = false
  promocodeData.value = { success: null, error: null }
  form.value.promocode = ''
  form.value.useDeposit = false
}

function selectPaymentType(option) {
  form.value.paymentType = option
  paymentSelectOpen.value = false
}

async function loadPaymentData() {
  settings.value = await BookingAPI.getSettings()
  apiKey.value = await BookingAPI.getPaymentApiKey()
  cards.value = await BookingAPI.getPaymentCards()
  buildPaymentTypes()
}

function buildPaymentTypes() {
  const nextPaymentTypes = []
  const paymentSettings = settings.value.modules?.payment_types
  const integrationType = paymentSettings?.online?.acquiring_integration_type

  if (paymentSettings?.online?.enable) {
    // Яндекс Пэй не поддерживает привязку карт — сохранённые карты не показываем
    if (integrationType !== 'yandex_pay') {
      cards.value.forEach((item) => {
        nextPaymentTypes.push({
          method: 'card',
          label: item.CardType,
          icon: item.CardType,
          card: item
        })
      })
    }

    nextPaymentTypes.push({
      method: 'new_card',
      label: 'Новая карта',
      icon: 'new_card'
    })
  }

  if (paymentSettings?.offline?.enable) {
    nextPaymentTypes.push({
      method: 'cash',
      label: 'Наличные',
      icon: 'cash'
    })
  }

  paymentTypes.value = nextPaymentTypes
  form.value.paymentType = nextPaymentTypes[0] || null
}

async function checkPromocode() {
  if (!props.bookingPayload?.serviceId || !form.value.promocode) {
    return
  }

  promocodeData.value = await BookingAPI.checkPromocode({
    promocode: form.value.promocode,
    service_id: props.bookingPayload.serviceId
  })

  if (promocodeData.value.success) {
    form.value.useDeposit = false
  }
}

function resetPromocode() {
  promocodeData.value = { success: null, error: null }
  form.value.promocode = ''
}

async function submitPayment() {
  if (!props.bookingPayload || !form.value.paymentType) {
    return
  }

  if (isPaymentDataLoading.value || (hasTimer.value && (timerExpired.value || timerSeconds.value <= 0))) {
    if (!errors.value.includes('Время на оплату истекло. Попробуйте забронировать снова.')) {
      errors.value.push('Время на оплату истекло. Попробуйте забронировать снова.')
    }
    return
  }

  isDisabledBtn.value = true
  errors.value = []
  const selectedPaymentType = form.value.paymentType

  const acquiringIntegrationTypeAtClick = settings.value.modules?.payment_types?.online?.acquiring_integration_type
  const willOpenAtmosTab = selectedPaymentType.method === 'new_card' && acquiringIntegrationTypeAtClick === 'atmos'
  const atmosPopup = willOpenAtmosTab ? openAtmosPopupTab() : null
  let atmosPopupHandedOff = false

  try {
    // Для наличной оплаты всегда синхронизируем статус на сервере явным cash-запросом:
    // это переводит запись в need_cash_payment / unconfirmed и не оставляет pending_payment.
    if (selectedPaymentType.method === 'cash') {
      const cashBookingResult = await BookingAPI.bookSlot({
        ...props.bookingPayload,
        payment_type: 'cash',
        deposit: form.value.useDeposit ? usableDeposit.value.value : undefined,
        promocode: promocodeData.value.success ? form.value.promocode : undefined
      })

      if (!cashBookingResult.success) {
        errors.value.push(cashBookingResult.error || 'Не удалось провести оплату. Попробуйте еще раз.')
        isDisabledBtn.value = false
        return
      }

      finishPaymentSuccess(selectedPaymentType.method)
      isDisabledBtn.value = false
      return
    }

    // Онлайн-ветка: создаем бронь/долг только один раз, повторные попытки оплаты
    // используют тот же debt_item и тот же дедлайн (таймер не сбрасывается).
    if (bookingDebtItem.value === null && bookingStatus.value !== 'paid' && bookingStatus.value !== 'success') {
      const bookingResult = await BookingAPI.bookSlot({
        ...props.bookingPayload,
        payment_type: 'online'
      })

      if (!bookingResult.success) {
        errors.value.push(bookingResult.error || 'Не удалось провести оплату. Попробуйте еще раз.')
        isDisabledBtn.value = false
        return
      }

      bookingDebtItem.value = bookingResult.debtItem || null
      bookingStatus.value = bookingResult.status || ''
      bookingAutoDeleteTime.value = Number(bookingResult.autoDeleteTime || 0)

      const deadlineMs = bookingAutoDeleteTime.value > 0 ? bookingAutoDeleteTime.value * 1000 : null
      startTimer(deadlineMs)
    }

    if (timerExpired.value || timerSeconds.value <= 0) {
      errors.value.push('Время на оплату истекло. Попробуйте забронировать снова.')
      isDisabledBtn.value = false
      return
    }

    if (!bookingDebtItem.value || bookingStatus.value === 'paid' || bookingStatus.value === 'success') {
      finishPaymentSuccess(selectedPaymentType.method)
      isDisabledBtn.value = false
      return
    }

    const useDeposit = form.value.useDeposit && Number(usableDeposit.value.value || 0) > 0
    const usePromocode = promocodeData.value.success === true && !!form.value.promocode
    if (!discountApplied.value && (useDeposit || usePromocode)) {
      const depositPayload = { debt_item_id: bookingDebtItem.value.id }
      if (useDeposit) depositPayload.deposit_amount = usableDeposit.value.value
      if (usePromocode) depositPayload.promocode = form.value.promocode

      const depositResult = await BookingAPI.payUseDeposit(depositPayload)
      if (!depositResult || depositResult.success !== true) {
        errors.value.push(depositResult?.error || 'Не удалось применить скидку. Попробуйте ещё раз.')
        isDisabledBtn.value = false
        return
      }

      discountApplied.value = true

      if (!depositResult.debt_item) {
        finishPaymentSuccess(useDeposit ? 'deposit' : selectedPaymentType.method)
        isDisabledBtn.value = false
        return
      }

      bookingDebtItem.value = depositResult.debt_item
    }

    const acquiringIntegrationType = settings.value.modules?.payment_types?.online?.acquiring_integration_type
    // ATMOS: withoutLink=0 нужен только для оплаты новой (непривязанной) картой по ссылке — для
    // оплаты уже привязанной картой (method === 'card') pay-link не нужен и создавать его нельзя:
    // его invoice_id пересекается с invoice_id последующего chargeToken() ("-1 Внутренняя ошибка").
    const isAtmosLinkPayment = acquiringIntegrationType === 'atmos' && selectedPaymentType.method !== 'card'
    const invoice = await BookingAPI.createInvoice({
      orders: bookingDebtItem.value.id,
      savecard: 1,
      email: form.value.email,
      cloudcheck: 1,
      withoutLink: isAtmosLinkPayment ? 0 : 1,
      returnUrl: getPaymentReturnUrl()
    })

    if (!invoice?.invoiceId) {
      errors.value.push('Не удалось провести оплату. Попробуйте еще раз.')
      isDisabledBtn.value = false
      return
    }

    const debtSum = bookingDebtItem.value.full_sum || bookingDebtItem.value.sum_full || {
      value: bookingDebtItem.value.sum,
      currency: bookingPrice.value.currency
    }

    capturedPaymentMethod = selectedPaymentType.method
    atmosPopupHandedOff = true
    const payResult = await pay(
      selectedPaymentType,
      invoice.invoiceId,
      debtSum.value,
      debtSum.currency,
      invoice.paymentLink ? { paymentLink: invoice.paymentLink } : invoice.JsonData,
      atmosPopup
    )
    if (payResult === 'redirect') {
      return
    }
    if (!payResult) {
      if (!atmosWait.value.active) {
        errors.value.push('Не удалось провести оплату. Попробуйте еще раз.')
        isDisabledBtn.value = false
      }
      return
    }

    finishPaymentSuccess(selectedPaymentType.method)
  } catch (error) {
    errors.value.push(error?.message || 'Не удалось провести оплату. Попробуйте еще раз.')
  } finally {
    if (atmosPopup && !atmosPopupHandedOff) {
      try {
        atmosPopup.close()
      } catch (e) {
        console.log('failed to close unused atmos tab', e)
      }
    }
  }

  isDisabledBtn.value = false
}

async function pay(paymentType, invoiceId, amount, currency, jsonData = {}, atmosPopup = null) {
  const paymentMethod = paymentType.method
  let payResult = false
  let integrationType = null

  if (paymentMethod === 'card') {
    const response = await BookingAPI.pay({
      invoice_id: invoiceId,
      card_id: paymentType.card.id
    })
    payResult = response.success === true
  } else if (paymentMethod === 'new_card') {
    integrationType = settings.value.modules?.payment_types?.online?.acquiring_integration_type

    if (integrationType === 'cloud_payments') {
      payResult = await cloudPaymentsPayWidget(apiKey.value, invoiceId, amount, currency, props.user?.id, form.value.email, jsonData)
    } else if (integrationType === 'payselection') {
      payResult = await payselectionPayWidget(invoiceId, amount, currency, props.user?.id, form.value.email, jsonData)
    } else if (integrationType === 'tip_top_pay') {
      payResult = await tipTopPayWidget(invoiceId, amount, currency, props.user?.id, form.value.email, jsonData)
    }  else if (integrationType === 'yookassa') {
      payResult = await yooKassaPayWidget(invoiceId, jsonData)
    }  else if (integrationType === 'yandex_pay') {
      return startYandexPayRedirect(jsonData, {
        scope: PAYMENT_SCOPE_BOOKING_WIDGET,
        invoiceId,
        booking: {
          paymentMethod,
          paymentAmount: priceTotal.value.value,
          paymentCurrency: priceTotal.value.currency,
          date: props.bookingPayload?.date,
          time: props.bookingPayload?.time,
          courtName: props.bookingPayload?.courtName,
          duration: props.bookingPayload?.duration,
          trainerName: props.bookingPayload?.trainerName
        }
      })
    } else if (integrationType === 'atmos') {
      const atmosResult = atmosRedirectPay(invoiceId, jsonData, atmosPopup)
      if (!atmosResult) {
        return false
      }
      if (atmosResult.mode === 'redirect') {
        return 'redirect'
      }
      return await atmosWaiter.start(
        atmosResult.popup,
        () => BookingAPI.checkInvoice({ save_card: 1, invoiceId }),
        jsonData.paymentLink
      )
    } else {
      console.log('unknown payment method - ', integrationType)
      payResult = false
    }
  }

  if (!payResult) {
    return false
  }

  const maxAttempts = 1
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const result = await BookingAPI.checkInvoice({
      save_card: 1,
      invoiceId
    })
    if (result || attempt === maxAttempts) {
      return result
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500))
  }

  return false
}

async function retryAtmosCheck() {
  const result = await atmosWaiter.retry()
  if (result) {
    finishPaymentSuccess(capturedPaymentMethod || 'new_card')
  }
}

function cancelAtmosWait() {
  atmosWaiter.cancel()
  isDisabledBtn.value = false
}

function reopenAtmosTab() {
  atmosWaiter.reopen()
}

function finishPaymentSuccess(paymentMethod) {
  stopTimer()
  emit('close')
  emit('success', {
    paymentMethod,
    paymentAmount: priceTotal.value.value,
    paymentCurrency: priceTotal.value.currency
  })
}

onUnmounted(() => {
  stopTimer()
  atmosWaiter.cancel()
})
</script>

<style src="@vueform/toggle/themes/default.css"></style>

<style scoped lang="scss">
.payment-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1750;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
}

.payment-modal {
  position: relative;
  width: min(480px, 100%);
  max-height: 95vh;
  overflow-y: auto;
  padding: 24px 20px 20px;
  border-radius: 24px;
  background: #FFFFFF;
  --toggle-bg-on: var(--booking-widget-primary-color, #3C67D6);
  --toggle-border-on: var(--booking-widget-primary-color, #3C67D6);
  --toggle-bg-off: #F3F5F8;
  --toggle-border-off: #F3F5F8;
  --toggle-border: 0;
  --toggle-width: 44px;
  --toggle-height: 24px;
}

.payment-modal-header {
  width: 100%;
  margin-bottom: 20px;
  color: #252525;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}

.payment-modal-close {
  position: absolute;
  top: 24px;
  right: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  background: #F3F5F8;
}

.payment-modal-close img,
.select-arrow img,
.select-item-active img {
  display: block;
}

.payment-form input {
  width: 100%;
  margin-bottom: 16px;
  padding: 17px 20px;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  outline: none;
  background: #F7F9FB;
  color: #252525;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
}

.payment-form input:focus {
  border-color: var(--booking-widget-primary-color, #3C67D6);
}

.payment-form input::placeholder {
  color: #9898A5;
  font-weight: 400;
}

.payment-form input:disabled {
  color: #252525;
  opacity: 1;
  cursor: default;
}

.booking-details-card {
  margin-bottom: 16px;
  padding: 20px;
  border-radius: 12px;
  background: #F7F9FB;
}

.booking-details-date {
  color: #252525;
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
}

.booking-details-time {
  margin-top: 4px;
  color: #9898A5;
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
}

.booking-details-court {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #9898A5;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
}

.booking-details-court img {
  width: 14px;
  height: 14px;
  display: block;
}

.select-field {
  position: relative;
  width: 100%;
}

.select-value {
  width: 100%;
  margin-bottom: 16px;
  padding: 17px 20px;
  box-sizing: border-box;
  border-radius: 12px;
  background: #F9FAFB;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.select-arrow {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #FFFFFF;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.select-items {
  position: absolute;
  top: 64px;
  left: 0;
  z-index: 10;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  display: none;
  border-radius: 12px;
  background: #FFFFFF;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.05);
}

.select-field.active .select-items {
  display: block;
}

.select-field.active .select-arrow {
  transform: rotate(90deg);
}

.select-item {
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.select-item:hover {
  background: #F7F9FB;
}

.select-item.active {
  background: #F3F5F8;
}

.payment-value {
  display: flex;
  align-items: center;
}

.payment-value-logo {
  width: 28px;
  min-width: 28px;
  height: 20px;
  margin-right: 17px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  line-height: 0;
}

.payment-value-logo img {
  display: block;
  max-width: 28px;
  max-height: 20px;
  width: auto;
  height: auto;
}

.payment-value-name {
  margin-right: 8px;
  font-weight: 400;
  line-height: 20px;
}

.payment-value-param {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
}

.promocode-field {
  position: relative;
}

.promocode-field.promocode-error input {
  border: 1px solid #ED3D3D;
}

.promocode-accept {
  position: absolute;
  top: 18px;
  right: 20px;
  background: none;
  background-color: transparent;
  color: var(--booking-widget-primary-color, #3C67D6);
  font-weight: 700;
}

.promocode-accept.disabled {
  color: #9898A5;
}

.promocode-reset {
  position: absolute;
  top: 14px;
  right: 16px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  background: #F3F5F8;
}

.promocode-reset img {
  display: block;
}

.promocode-error-text {
  margin: -8px 0 16px 0;
  color: red;
  font-size: 12px;
}

.promocode-success-text {
  margin: -8px 0 16px 0;
  color: #4EB410;
  font-size: 12px;
  line-height: 16px;
}

.bonus-field,
.total-field {
  margin-bottom: 16px;
  padding: 20px;
  border-radius: 12px;
  background: #F7F9FB;
}

.bonus-field {
  display: flex;
  justify-content: space-between;
}

.bonus-field-control {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.bonus-field-value {
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
}

.bonus-field-value small {
  display: block;
  color: #9898A5;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
}

.bonus-field-value small span {
  display: block;
}

.bonus-field-control :deep(.toggle-container) {
  display: flex;
  align-items: center;
  line-height: 0;
  vertical-align: middle;
}

.bonus-field-control :deep(.toggle-container:focus) {
  box-shadow: none;
  outline: none;
}

.bonus-field-control :deep(.toggle) {
  flex-shrink: 0;
}

.total-field-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  line-height: 20px;
}

.total-field-row .key.discount {
  color: #9898A5;
}

.total-field-row .key.discount,
.total-field-row .value.red {
  font-size: 12px;
  line-height: 16px;
}

.total-field-row .value.red {
  color: red;
}

.total-field-row:last-child .value {
  font-weight: 600;
}

.payment-submit-button {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
}

.payment-timer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #FFF7EB;
}

.payment-timer-text {
  color: #A35E00;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
}

.payment-timer-badge {
  min-width: 56px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #FFFFFF;
  background: #F19819;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.payment-submit-button.disabled {
  opacity: 0.5;
}

.payment-atmos-wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  text-align: center;
}

.payment-atmos-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 16px;
  border: 3px solid #E4E7F2;
  border-top-color: #3C67D6;
  border-radius: 50%;
  animation: payment-atmos-spin 0.8s linear infinite;
}

@keyframes payment-atmos-spin {
  to {
    transform: rotate(360deg);
  }
}

.payment-expired-alert {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  text-align: center;
}

.payment-expired-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: #FFF7EB;
}

.payment-expired-title {
  margin: 0 0 8px;
  color: #191B26;
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
}

.payment-expired-text {
  margin: 0 0 24px;
  color: #757885;
  font-size: 15px;
  line-height: 22px;
}

.payment-expired-cancel {
  margin-top: 16px;
  border: none;
  background: transparent;
  color: #3C67D6;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.payment-hint {
  margin-top: 20px;
  color: #757885;
  font-size: 12px;
  line-height: 16px;
}

.payment-hint a {
  color: var(--booking-widget-primary-color, #3C67D6);
}

.payment-hint a:hover {
  text-decoration: underline;
}

.payment-errors {
  margin-top: 16px;
  padding: 20px;
  border-radius: 12px;
  background: red;
  color: #FFFFFF;
}

.payment-errors ul,
.payment-errors li {
  list-style: none;
}

</style>
